/**
 * Financial tab, payments part: payments KPIs, money collected over time,
 * payment methods and payment statuses as cards.
 */
import { Box, Card, CardContent, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import type { FinancialReportResponse } from '../../store/api/reportsApi';
import { formatCurrency, formatNumber } from '../../utils/format';

type FinancialData = FinancialReportResponse['data'];

const percent = (part: number, whole: number) => (whole ? Math.round((part * 100) / whole) : 0);

const Heading = ({ title, hint }: { title: string; hint?: string }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="h6">{title}</Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

type Period = { date_from?: string; date_to?: string };
type ChartPoint = { label: string; revenue: number; count: number };

/**
 * One point per day (or month) across the whole period, zeros included, so a
 * quiet day shows as a dip instead of disappearing from the chart.
 */
const buildSeries = (data: FinancialData, period: Period, monthly: boolean): ChartPoint[] => {
  const unit = monthly ? 'month' : 'day';
  const keyFormat = monthly ? 'YYYY-MM' : 'YYYY-MM-DD';
  const values = new Map<string, { revenue: number; count: number }>();
  if (monthly) data.monthly_revenue.forEach((m) => values.set(m.month, { revenue: Number(m.revenue), count: m.count }));
  else data.daily_revenue.forEach((d) => values.set(moment(d.date).format(keyFormat), { revenue: Number(d.revenue), count: d.count }));

  const keys = [...values.keys()].sort();
  if (!keys.length) return [];
  const start = moment(period.date_from || keys[0], keyFormat).startOf(unit);
  let end = moment(period.date_to || keys[keys.length - 1], keyFormat).startOf(unit);
  if (end.isAfter(moment())) end = moment().startOf(unit); // never draw the future
  if (!start.isValid() || end.isBefore(start)) return [];

  const points: ChartPoint[] = [];
  for (const cursor = start.clone(); !cursor.isAfter(end) && points.length < 400; cursor.add(1, unit)) {
    const v = values.get(cursor.format(keyFormat));
    points.push({ label: cursor.format(monthly ? 'MMM YYYY' : 'DD/MM'), revenue: v?.revenue ?? 0, count: v?.count ?? 0 });
  }
  return points;
};

const compact = (value: number) => formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 });

const FinancialSections = ({ data, period = {} }: { data: FinancialData; period?: Period }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { overview } = data;

  const statusColor: Record<string, string> = {
    COMPLETED: theme.palette.success.main,
    PENDING: theme.palette.warning.main,
    PROCESSING: theme.palette.info.main,
    FAILED: theme.palette.error.main,
    REFUNDED: theme.palette.grey[500],
    PARTIALLY_REFUNDED: theme.palette.grey[500],
  };
  const methodLabel = (method: string) => t(`reports.finance.methods.${method}`, { defaultValue: method });

  // Daily points for short periods, monthly for long ones
  const daySpan =
    period.date_from && period.date_to
      ? moment(period.date_to).diff(moment(period.date_from), 'days') + 1
      : data.daily_revenue.length;
  const useMonthly = daySpan > 62;
  const chartData = buildSeries(data, period, useMonthly);
  const chartTotal = chartData.reduce((sum, p) => sum + p.revenue, 0);
  const chartCount = chartData.reduce((sum, p) => sum + p.count, 0);
  const best = chartData.reduce<ChartPoint | null>((top, p) => (p.revenue > (top?.revenue ?? 0) ? p : top), null);
  const green = theme.palette.success.main;
  const tooltipProps = {
    formatter: (value: unknown, _name: unknown, item: { payload?: ChartPoint }) => [
      `${formatCurrency(Number(value))} · ${t('reports.finance.paymentsN', { count: item?.payload?.count ?? 0 })}`,
      t('reports.finance.collected'),
    ],
    contentStyle: { borderRadius: 8, border: `1px solid ${theme.palette.divider}`, background: theme.palette.background.paper },
  };

  const collectedTotal = data.method_breakdown.reduce((sum, m) => sum + m.completed_amount, 0);
  const paymentsTotal = data.status_breakdown.reduce((sum, s) => sum + s.count, 0);

  const kpis = [
    { label: t('reports.finance.paymentsCount'), value: formatNumber(overview.total_payments) },
    { label: t('reports.finance.avgPayment'), value: formatCurrency(overview.average_payment) },
    { label: t('reports.finance.pending'), value: formatCurrency(overview.pending_amount), color: overview.pending_amount ? theme.palette.warning.main : undefined },
    { label: t('reports.finance.failed'), value: formatCurrency(overview.failed_amount), color: overview.failed_amount ? theme.palette.error.main : undefined },
  ];

  return (
    <Box>
      <Heading title={t('reports.finance.paymentsTitle')} hint={t('reports.finance.paymentsHint')} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {k.label}
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: k.color }}>
                {k.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Collected over time */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {t('reports.finance.collectedOverTime')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {useMonthly ? t('reports.mgmt.overview.bucket.month') : t('reports.mgmt.overview.bucket.day')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('reports.finance.collected')}
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {formatCurrency(chartTotal)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('reports.finance.paymentsCount')}
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {formatNumber(chartCount)}
                </Typography>
              </Box>
              {best && chartData.length > 1 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {useMonthly ? t('reports.finance.bestMonth') : t('reports.finance.bestDay')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {best.label} · {compact(best.revenue)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          {chartCount ? (
            // SVG tick labels collide with the axis under RTL, so the chart itself stays LTR
            <Box dir="ltr" sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartData.length > 1 ? (
                  <AreaChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="collectedFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={green} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={green} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                    <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} minTickGap={16} />
                    <YAxis fontSize={11} width={48} tickLine={false} axisLine={false} tickFormatter={compact} />
                    <Tooltip {...tooltipProps} />
                    <Area
                      isAnimationActive={false}
                      type="monotone"
                      dataKey="revenue"
                      stroke={green}
                      strokeWidth={2.5}
                      fill="url(#collectedFill)"
                      dot={chartData.length <= 31 ? { r: 3, fill: green, strokeWidth: 0 } : false}
                      activeDot={{ r: 5 }}
                    />
                  </AreaChart>
                ) : (
                  <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                    <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} width={48} tickLine={false} axisLine={false} tickFormatter={compact} />
                    <Tooltip {...tooltipProps} cursor={{ fill: theme.palette.action.hover }} />
                    <Bar isAnimationActive={false} dataKey="revenue" fill={green} radius={[6, 6, 0, 0]} maxBarSize={56} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              {t('reports.detailed.noData')}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Payment methods */}
        <Box>
          <Heading title={t('reports.finance.methodsTitle')} hint={t('reports.finance.methodsHint')} />
          <Box sx={{ display: 'grid', gap: 2 }}>
            {data.method_breakdown.length === 0 && (
              <Typography color="text.secondary">{t('reports.detailed.noData')}</Typography>
            )}
            {data.method_breakdown.map((m) => {
              const share = percent(m.completed_amount, collectedTotal);
              return (
                <Card key={m.payment_method}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {methodLabel(m.payment_method)}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="success.main">
                        {formatCurrency(m.completed_amount)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={share}
                      color="success"
                      sx={{ my: 1.5, height: 6, borderRadius: 3 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.finance.methodShare', { percent: share })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.finance.methodCounts', { completed: m.completed_count, total: m.count })}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* Payment statuses */}
        <Box>
          <Heading title={t('reports.finance.statusTitle')} hint={t('reports.finance.statusHint')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {data.status_breakdown.length === 0 && (
              <Typography color="text.secondary">{t('reports.detailed.noData')}</Typography>
            )}
            {data.status_breakdown.map((s) => {
              const color = statusColor[s.status] ?? theme.palette.grey[500];
              const share = percent(s.count, paymentsTotal);
              return (
                <Card key={s.status} sx={{ borderTop: `4px solid ${color}` }}>
                  <CardContent>
                    <Typography variant="body2" fontWeight={600} sx={{ color }}>
                      {t(`orders.timeline.payment.${s.status}`, { defaultValue: s.status })}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
                      <Typography variant="h4" fontWeight={700}>
                        {formatNumber(s.count)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.finance.statusShare', { percent: share })}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={share}
                      sx={{ my: 1.5, height: 6, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color } }}
                    />
                    <Typography variant="body1" fontWeight={600}>
                      {formatCurrency(s.total_amount)}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FinancialSections;
